/** KB evaluation cadence — fixed 15 min (docs/reference/enterprise-architecture.md). */
export const KB_TICK_INTERVAL_MS = 15 * 60 * 1000;

export type KbRunTiming =
  | { status: "never" }
  | {
      status: "scheduled" | "overdue";
      lastFinishedAt: Date;
      nextAt: Date;
      remainingMs: number;
    };

/**
 * Next KB run estimate from the latest tick: finished_at + 15 min.
 * Overdue when that instant is already past (scheduler delayed/stopped) — never a negative clock.
 */
export function deriveKbRunTiming(
  lastFinishedAtIso: string | null | undefined,
  nowMs: number = Date.now(),
): KbRunTiming {
  if (!lastFinishedAtIso) return { status: "never" };
  const lastFinishedAt = new Date(lastFinishedAtIso);
  const lastMs = lastFinishedAt.getTime();
  if (Number.isNaN(lastMs)) return { status: "never" };

  const nextAt = new Date(lastMs + KB_TICK_INTERVAL_MS);
  const remainingMs = nextAt.getTime() - nowMs;
  if (remainingMs <= 0) {
    return {
      status: "overdue",
      lastFinishedAt,
      nextAt,
      remainingMs: 0,
    };
  }
  return {
    status: "scheduled",
    lastFinishedAt,
    nextAt,
    remainingMs,
  };
}

/** Format remaining ms as mm:ss for the Seuraava ajo countdown. */
export function formatCountdown(remainingMs: number): {
  mm: string;
  ss: string;
} {
  const totalSec = Math.max(0, Math.ceil(remainingMs / 1000));
  const mm = String(Math.floor(totalSec / 60)).padStart(2, "0");
  const ss = String(totalSec % 60).padStart(2, "0");
  return { mm, ss };
}

const CLOCK_FI: Intl.DateTimeFormatOptions = {
  hour: "2-digit",
  minute: "2-digit",
  // h23: midnight is 00:00. fi-FI can otherwise pick h24 and print 24:00.
  hourCycle: "h23",
  hour12: false,
};

function asValidDate(isoOrDate: string | Date): Date | null {
  const d = typeof isoOrDate === "string" ? new Date(isoOrDate) : isoOrDate;
  return Number.isNaN(d.getTime()) ? null : d;
}

/** Clock-only Finnish local time — axis labels, never 24:00. */
export function formatClockFi(isoOrDate: string | Date): string {
  const d = asValidDate(isoOrDate);
  if (!d) return "—";
  return d.toLocaleTimeString("fi-FI", CLOCK_FI);
}

export function formatLastRunFi(isoOrDate: string | Date): string {
  const d = asValidDate(isoOrDate);
  if (!d) return "—";
  return d.toLocaleString("fi-FI", {
    day: "numeric",
    month: "numeric",
    ...CLOCK_FI,
  });
}

/**
 * Right edge of the 24 h ribbon. A live loop's last tick is ~now; a paused loop
 * or a manual feed should show the last sweep, not empty wall-clock hours after
 * it. History scroll later is the same parameter with a chosen end.
 */
export function cycleRibbonEndMs(
  lastEvaluatedAtIso: string | null | undefined,
  nowMs: number = Date.now(),
): number {
  const d = lastEvaluatedAtIso ? asValidDate(lastEvaluatedAtIso) : null;
  if (!d) return nowMs;
  return Math.min(d.getTime(), nowMs);
}

/** Same local calendar date — the day-break divider needs this, not just clock time. */
export function isSameLocalDate(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

/** Date-only Finnish local date — the day-break marker, never a time. */
export function formatDateFi(isoOrDate: string | Date): string {
  const d = asValidDate(isoOrDate);
  if (!d) return "—";
  return d.toLocaleDateString("fi-FI", { day: "numeric", month: "numeric" });
}

/** Five labels along a sliding 24 h window (ends inclusive). Clock-only: which calendar day
 * a tick falls on is the day-break divider's job (kb-cycle-slots.ts dayBreakSlotIndex), not
 * this axis. */
export function cycleAxisLabels(
  windowStart: Date,
  windowEnd: Date,
  tickCount: number = 5,
): string[] {
  const count = Math.max(2, tickCount);
  const span = windowEnd.getTime() - windowStart.getTime();
  return Array.from({ length: count }, (_, i) =>
    formatClockFi(new Date(windowStart.getTime() + (span * i) / (count - 1))),
  );
}
